import { useRef, cloneElement } from 'react'
import clsx from 'clsx'
import { useTabList, useTab, useTabPanel } from '@react-aria/tabs'
import { Item } from '@react-stately/collections'
import { useTabListState } from '@react-stately/tabs'
import { mergeProps } from '@react-aria/utils'
import { useFocusRing } from '@react-aria/focus'

const tryMergingRefs = (refs) => (value) => {
  refs.forEach((ref) => {
    if (typeof ref === 'function') {
      ref(value)
    } else if (ref != null) {
      ref.current = value
    }
  })
}

function Tab({ item, state }) {
  const { key, rendered } = item
  const ref = useRef()
  const { tabProps } = useTab({ key }, state, ref)
  const isSelected = state.selectedKey === key
  const isDisabled = state.disabledKeys.has(key)
  const { isFocusVisible, focusProps } = useFocusRing()

  const props = mergeProps(tabProps, focusProps, {
    className: clsx('focus:outline-none focus-visible:outline-none', {
      'ring-2 ring-primary': isFocusVisible,
    }),
  })

  const children =
    typeof rendered === 'function'
      ? rendered({ isSelected, isDisabled })
      : rendered

  return typeof rendered === 'function' ? (
    cloneElement(
      children,
      mergeProps(props, children.props, {
        ref: tryMergingRefs([children.ref, (el) => (ref.current = el)]),
      })
    )
  ) : (
    <div {...props} ref={ref}>
      {children}
    </div>
  )
}

function Tabs({ tabs, tabContainerClassName, tabContentClassName, ...props }) {
  const state = useTabListState({
    ...props,
    children: tabs?.map((tab) => (
      <Item title={tab.title} key={tab.key}>
        {tab.content}
      </Item>
    )),
  })
  const ref = useRef()
  const { tabListProps } = useTabList(props, state, ref)

  let tabPanelRef = useRef()
  let { tabPanelProps } = useTabPanel(
    { key: state.selectedItem?.key },
    state,
    tabPanelRef
  )

  return (
    <>
      <div
        {...tabListProps}
        ref={ref}
        className={clsx('flex', tabContainerClassName)}
      >
        {[...state.collection].map((item) => (
          <Tab key={item.key} item={item} state={state} />
        ))}
      </div>
      <div {...tabPanelProps} ref={tabPanelRef} className={tabContentClassName}>
        {state.selectedItem?.props.children}
      </div>
    </>
  )
}

export default Tabs
